/**
 * Éditeur de texte riche amélioré avec gestion avancée des listes
 */
import { RichEditor } from './RichEditor.js';
import { ListManager } from './ListManager.js';
import { KeyboardHandler } from './KeyboardHandler.js';
import { UndoRedoManager } from './UndoRedoManager.js';

export class EnhancedRichEditor extends RichEditor {
    constructor(container) {
        super(container);
        this.listManager = new ListManager(this);
        this.keyboardHandler = new KeyboardHandler(this);
        this.undoRedoManager = new UndoRedoManager(this);
        this.initializeListFeatures();
        this.initializeAdvancedFeatures();
    }
    
    initializeListFeatures() {
        this.initializeListButtons();
    }
    
    initializeAdvancedFeatures() {
        this.initializeUndoRedoButtons();
        this.initializePasteHandler();
    }
    
    initializeListButtons() {
        // Remplacer les anciens gestionnaires de listes
        const ulButton = this.container.querySelector('button.ui-button.ul');
        if (ulButton) {
            // Supprimer les anciens event listeners
            ulButton.replaceWith(ulButton.cloneNode(true));
            const newUlButton = this.container.querySelector('button.ui-button.ul');
            
            newUlButton.addEventListener('click', event => {
                event.preventDefault();
                this.listManager.insertUnorderedList();
            });
        }
        
        const olButton = this.container.querySelector('button.ui-button.ol');
        if (olButton) {
            // Supprimer les anciens event listeners
            olButton.replaceWith(olButton.cloneNode(true));
            const newOlButton = this.container.querySelector('button.ui-button.ol');
            
            newOlButton.addEventListener('click', event => {
                event.preventDefault();
                this.listManager.insertOrderedList();
            });
        }
        
        const indentButton = this.container.querySelector('button.ui-button.indent');
        if (indentButton) {
            // Supprimer les anciens event listeners
            indentButton.replaceWith(indentButton.cloneNode(true));
            const newIndentButton = this.container.querySelector('button.ui-button.indent');
            
            newIndentButton.addEventListener('click', event => {
                event.preventDefault();
                this.listManager.indent();
            });
        }
        
        const outdentButton = this.container.querySelector('button.ui-button.outdent');
        if (outdentButton) {
            // Supprimer les anciens event listeners
            outdentButton.replaceWith(outdentButton.cloneNode(true));
            const newOutdentButton = this.container.querySelector('button.ui-button.outdent');
            
            newOutdentButton.addEventListener('click', event => {
                event.preventDefault();
                this.listManager.outdent();
            });
        }
    }
    
    initializeUndoRedoButtons() {
        const undoButton = this.container.querySelector('button.ui-button.undo');
        if (undoButton) {
            undoButton.addEventListener('click', event => {
                event.preventDefault();
                this.undoRedoManager.undo();
            });
        }
        
        const redoButton = this.container.querySelector('button.ui-button.redo');
        if (redoButton) {
            redoButton.addEventListener('click', event => {
                event.preventDefault();
                this.undoRedoManager.redo();
            });
        }
    }
    
    initializePasteHandler() {
        this.editorContent.addEventListener('paste', event => {
            event.preventDefault();
            
            const clipboardData = event.clipboardData || window.clipboardData;
            const htmlData = clipboardData.getData('text/html');
            const textData = clipboardData.getData('text/plain');
            
            if (htmlData) {
                // Nettoyer le HTML collé
                const cleanedHtml = this.cleanPastedHtml(htmlData);
                this.insertCleanHtml(cleanedHtml);
            } else if (textData) {
                // Insérer le texte brut
                this.insertText(textData);
            }
        });
    }
    
    cleanPastedHtml(html) {
        // Créer un élément temporaire pour nettoyer le HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        // Supprimer les styles inline et attributs indésirables
        const elementsWithStyle = temp.querySelectorAll('[style]');
        elementsWithStyle.forEach(el => el.removeAttribute('style'));
        
        // Supprimer les classes CSS
        const elementsWithClass = temp.querySelectorAll('[class]');
        elementsWithClass.forEach(el => el.removeAttribute('class'));
        
        // Conserver uniquement les balises autorisées
        const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
        const allElements = temp.querySelectorAll('*');
        
        allElements.forEach(el => {
            if (!allowedTags.includes(el.tagName.toLowerCase())) {
                // Remplacer les balises non autorisées par leur contenu
                const textNode = document.createTextNode(el.textContent);
                el.replaceWith(textNode);
            }
        });
        
        return temp.innerHTML;
    }
    
    insertCleanHtml(html) {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        const fragment = document.createDocumentFragment();
        while (temp.firstChild) {
            fragment.appendChild(temp.firstChild);
        }
        
        range.insertNode(fragment);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }
    
    insertText(text) {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        // Remplacer les retours à la ligne par des paragraphes
        const paragraphs = text.split('\n\n');
        const fragment = document.createDocumentFragment();
        
        paragraphs.forEach((paragraphText, index) => {
            if (paragraphText.trim()) {
                const p = document.createElement('p');
                p.textContent = paragraphText.trim();
                fragment.appendChild(p);
            }
            
            if (index < paragraphs.length - 1) {
                fragment.appendChild(document.createElement('br'));
            }
        });
        
        range.insertNode(fragment);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }
    
    // Améliorer la détection du contexte de liste pour la barre d'outils
    updateBlockTypeSelect() {
        super.updateBlockTypeSelect();
        this.updateListButtonStates();
    }
    
    updateListButtonStates() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        let currentElement = range.commonAncestorContainer;
        
        if (currentElement.nodeType === Node.TEXT_NODE) {
            currentElement = currentElement.parentNode;
        }
        
        const listType = this.listManager.getListType(currentElement);
        const listItem = this.listManager.getListItem(currentElement);
        
        // Mettre à jour l'état des boutons de liste
        const ulButton = this.container.querySelector('button.ui-button.ul');
        const olButton = this.container.querySelector('button.ui-button.ol');
        const indentButton = this.container.querySelector('button.ui-button.indent');
        const outdentButton = this.container.querySelector('button.ui-button.outdent');
        
        if (ulButton) {
            ulButton.classList.toggle('is-active', listType === 'ul');
        }
        
        if (olButton) {
            olButton.classList.toggle('is-active', listType === 'ol');
        }
        
        if (indentButton) {
            indentButton.disabled = !listItem;
        }
        
        if (outdentButton) {
            outdentButton.disabled = !listItem;
        }
    }
}